import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import SectionHeader from "../SectionHeader";
import { usePersonaData } from "./usePersonaData";
import { Check } from "lucide-react";

interface PersonaCardProps {
    icon: React.ReactNode;
    title: string;
    description: string;
    subtitle: string;
    recommendedProduct: string;
    color: string;
    index?: number;
    benefits: string[];
}

const PersonaCard: React.FC<PersonaCardProps> = ({ icon, title, description, subtitle, recommendedProduct, benefits }) => {
    const { i18n } = useTranslation();
    const isRTL = ["fa", "ar"].includes(i18n.language);

    return (
        <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="h-full flex flex-col"
            dir={isRTL ? "rtl" : "ltr"}
        >
            <div className="bg-white rounded-2xl p-6 border border-slate-200/80 shadow-xs hover:shadow-md transition-all duration-300 flex-grow flex flex-col justify-between relative overflow-hidden group">
                
                <div>
                    {/* Header: Icon & Title */}
                    <div className="flex items-start gap-4 mb-4">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0038A8] to-[#0072CE] text-white flex items-center justify-center shrink-0 shadow-xs [&>svg]:w-6 [&>svg]:h-6 [&>svg]:m-auto">
                            {icon}
                        </div>

                        <div className="flex flex-col min-w-0">
                            <h3 className="text-base md:text-lg font-bold text-slate-900 group-hover:text-[#0038A8] transition-colors duration-200 truncate">
                                {title}
                            </h3>
                            <p className="text-slate-500 text-xs font-medium mt-0.5">
                                {subtitle}
                            </p>
                        </div>
                    </div>

                    {/* Description */}
                    <p className="text-slate-600 text-sm leading-relaxed mb-4">
                        {description}
                    </p>

                    {/* Benefits List (Clean Inline - No Modals) */}
                    {benefits && benefits.length > 0 && (
                        <div className="pt-3 border-t border-slate-100">
                            <ul className="space-y-2">
                                {benefits.map((benefit, i) => (
                                    <li key={i} className="flex items-center gap-2 text-slate-700 text-xs font-medium">
                                        <div className="w-4 h-4 rounded-full bg-blue-50 text-[#0072CE] flex items-center justify-center shrink-0">
                                            <Check className="w-2.5 h-2.5 stroke-[3]" />
                                        </div>
                                        <span>{benefit}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                {/* Footer Recommended Product Label */}
                <div className="mt-5 pt-3 border-t border-slate-100/80 flex items-center justify-between text-xs">
                    <span className="text-slate-400 font-medium">پیشنهاد مناسب:</span>
                    <span className="px-2.5 py-1 rounded-lg bg-blue-50 text-[#0038A8] font-semibold border border-blue-100/60">
                        {recommendedProduct}
                    </span>
                </div>

                {/* Subtle Hover Accent */}
                <div className="absolute bottom-0 inset-x-0 h-0.5 bg-gradient-to-r from-[#0038A8] to-[#0072CE] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
        </motion.div>
    );
};

const PersonaGrid: React.FC = () => {
    const { t, i18n } = useTranslation("Persona");
    const isRTL = ["fa", "ar"].includes(i18n.language);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const { personas } = usePersonaData();

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 1024);
        };
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    const itemsToShow = isMobile ? 1 : 3;
    const totalSlides = Math.ceil(personas.length / itemsToShow);

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % totalSlides);
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    };

    const startIndex = currentSlide * itemsToShow;
    const visiblePersonas = personas.slice(startIndex, startIndex + itemsToShow);

    return (
        <section dir={isRTL ? "rtl" : "ltr"} className="py-12 relative overflow-hidden">
            <div className="container mx-auto px-4 sm:px-10 relative z-10">
                <SectionHeader
                    className="pb-6"
                    regularText={t("header.regularText")}
                    highlightedText={t("header.highlightedText")}
                    descriptionHighlight={t("header.descriptionHighlight")}
                    descriptionText={t("header.descriptionText")}
                />

                <div className="relative">
                    <div className={`grid grid-cols-1 ${isMobile ? "" : "lg:grid-cols-3"} gap-6`}>
                        {visiblePersonas.map((persona) => (
                            <PersonaCard key={persona.title} {...persona} />
                        ))}
                    </div>

                    {personas.length > itemsToShow && (
                        <div className="flex justify-center items-center gap-3 mt-8">
                            <button
                                onClick={prevSlide}
                                className="w-9 h-9 rounded-full bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors flex items-center justify-center shadow-2xs cursor-pointer"
                                aria-label={t("navigation.prev")}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 rtl:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>

                            {/* Pagination Dots */}
                            <div className="flex items-center gap-1">
                                {Array.from({ length: totalSlides }).map((_, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setCurrentSlide(idx)}
                                        className="p-3 flex items-center justify-center cursor-pointer touch-manipulation"
                                        aria-label={`اسلاید ${idx + 1}`}
                                    >
                                        <span
                                            className={`h-2 rounded-full transition-all duration-300 block ${
                                                currentSlide === idx ? "w-6 bg-[#0072CE]" : "w-2 bg-slate-300 hover:bg-slate-400"
                                            }`}
                                        />
                                    </button>
                                ))}
                            </div>

                            <button
                                onClick={nextSlide}
                                className="w-9 h-9 rounded-full bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors flex items-center justify-center shadow-2xs cursor-pointer"
                                aria-label={t("navigation.next")}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 rtl:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default PersonaGrid;
